import React, { useState } from 'react';
import { Plus } from 'lucide-react';

const CARD_BG = '#221A12';
const CARD_BG_HOVER = '#221A12';
const HAIRLINE = 'rgba(255,236,206,.06)';
const HEADING = '#F5EDE0';
const MUTED = '#C8BFB1';
const GREEN = '#55BA3F';
const GREEN_BRIGHT = '#6FE04F';

const MenuItemCard = ({ item, onAddToCart, isOrderingOpen = true }) => {
  const [adding, setAdding] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  const handleAdd = async () => {
    setAdding(true);
    try {
      await onAddToCart(item);
    } finally {
      setTimeout(() => setAdding(false), 600);
    }
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(price);

  return (
    <div
      data-testid={`menu-item-${item.id}`}
      className="group cursor-pointer transition-all duration-300 hover:-translate-y-1"
      style={{
        borderRadius: '1.5rem',
        background: CARD_BG,
        border: `1px solid ${HAIRLINE}`,
        boxShadow: '0 1px 0 rgba(255,255,255,.02) inset',
      }}
      onMouseEnter={e => (e.currentTarget.style.background = CARD_BG_HOVER)}
      onMouseLeave={e => (e.currentTarget.style.background = CARD_BG)}
    >
      {/* Image intentionally hidden - list/row layout */}

      {/* Content */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3
            className="text-base font-medium tracking-tight leading-snug"
            style={{ color: HEADING, fontFamily: 'Outfit, sans-serif' }}
          >
            {item.name}
          </h3>
          <span
            className="text-base font-semibold tracking-tight shrink-0"
            style={{ color: GREEN_BRIGHT, fontFamily: 'Outfit, sans-serif' }}
          >
            {item.priceMatrix ? `From ${formatPrice(item.price)}` : formatPrice(item.price)}
          </span>
        </div>

        {item.description && (
          <p
            className={`text-sm leading-relaxed ${item.priceMatrix ? '' : 'line-clamp-2'} mb-4`}
            style={{ color: MUTED }}
          >
            {item.description}
          </p>
        )}

        {/* Price matrix table */}
        {item.priceMatrix && Array.isArray(item.priceMatrix.rows) && (
          <div
            data-testid={`price-matrix-${item.id}`}
            className="mb-4 rounded-xl overflow-hidden"
            style={{ background: 'rgba(255,236,206,.03)', border: `1px solid ${HAIRLINE}` }}
          >
            {item.priceMatrix.title && (
              <div
                className="px-3 py-2 text-[11px] font-semibold tracking-[.14em] uppercase"
                style={{
                  color: GREEN_BRIGHT,
                  background: 'rgba(85,186,63,.08)',
                  fontFamily: 'Outfit, sans-serif',
                  borderBottom: `1px solid ${HAIRLINE}`,
                }}
              >
                {item.priceMatrix.title}
              </div>
            )}
            <table className="w-full text-xs" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <thead>
                <tr>
                  {(item.priceMatrix.columns || []).map((col, i) => (
                    <th
                      key={i}
                      className={`px-3 py-2 font-semibold ${i === 0 ? 'text-left' : 'text-right'}`}
                      style={{ color: HEADING, borderBottom: `1px solid ${HAIRLINE}` }}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {item.priceMatrix.rows.map((row, ri) => (
                  <tr key={ri} style={{ borderTop: ri === 0 ? 'none' : `1px solid ${HAIRLINE}` }}>
                    {row.map((cell, ci) => (
                      <td
                        key={ci}
                        className={`px-3 py-2 ${ci === 0 ? 'text-left font-medium' : 'text-right tabular-nums'}`}
                        style={{ color: ci === 0 ? HEADING : GREEN_BRIGHT }}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Tags */}
        {item.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {item.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="px-2.5 py-0.5 rounded-full text-xs"
                style={{ background: 'rgba(255,236,206,.05)', color: MUTED, border: `1px solid ${HAIRLINE}` }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Bottom row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {item.dietary?.length > 0 && (
              <span className="text-xs" style={{ color: MUTED }}>
                {item.dietary.map(d => d === 'vegetarian' ? 'V' : d === 'vegan' ? 'VG' : d === 'gluten-free' ? 'GF' : d.charAt(0).toUpperCase()).join(' · ')}
              </span>
            )}
          </div>

          {isOrderingOpen && (
            <button
              data-testid={`add-to-cart-${item.id}`}
              onClick={handleAdd}
              disabled={adding}
              className="flex items-center justify-center w-9 h-9 rounded-full transition-all duration-200"
              style={{
                background: adding ? GREEN : 'rgba(255,236,206,.06)',
                color: adding ? '#000' : HEADING,
                border: `1px solid ${adding ? GREEN : 'rgba(255,236,206,.14)'}`,
                boxShadow: adding ? '0 4px 14px rgba(85,186,63,.5)' : 'none',
              }}
            >
              <Plus size={18} className={`transition-transform duration-200 ${adding ? 'rotate-45' : ''}`} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MenuItemCard;
